import React, { useState, useEffect, useCallback } from 'react';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const testimonials = [
  {
    quote: "RunTheRays helped us source and ship 200+ Jetour vehicles from Shanghai to Accra in record time. Their quality inspections ensured every unit met our standards before leaving port.",
    name: "Daniel Mensah",
    title: "Procurement Director",
    company: "Jetour Ghana",
    industry: "Automobile",
    rating: 5
  },
  {
    quote: "We've been working with RunTheRays for over two years on our electronics sourcing. Their factory audits and quality control process saved us from a $150K defective shipment last year.",
    name: "Sarah Owusu",
    title: "Supply Chain Manager",
    company: "TechBridge West Africa",
    industry: "Electronics"
  },
  {
    quote: "Their understanding of Shanghai port logistics is unmatched. From customs clearance to last-mile delivery, RunTheRays handles everything so we can focus on growing our business.",
    name: "Kwame Asante",
    title: "CEO",
    company: "GoldCoast Industrial Supplies",
    industry: "Industrial Equipment"
  },
  {
    quote: "The sourcing wizard made it incredibly easy to get started. Within 48 hours we had vetted supplier options with pricing. RunTheRays is our go-to partner for China imports.",
    name: "Amina Toure",
    title: "Operations Lead",
    company: "Sahel Trading Group",
    industry: "Consumer Goods"
  },
  {
    quote: "RunTheRays managed our entire import/export compliance process including documentation, HS codes, and customs declarations. Zero delays across 12 shipments this year.",
    name: "Michael Addo",
    title: "Logistics Coordinator",
    company: "Atlantic Freight Solutions",
    industry: "Logistics"
  }
];

const partnerLogos = [
  { name: "Jetour Auto", logo: `${API}/api/cars/images/logos/jetour_auto.jpg` },
  { name: "TechBridge", logo: `${API}/api/cars/images/logos/techbridge.jpg` },
  { name: "GoldCoast Industrial", logo: `${API}/api/cars/images/logos/goldcoast.jpg` },
  { name: "Sahel Trading", logo: `${API}/api/cars/images/logos/sahel.jpg` },
  { name: "Atlantic Freight", logo: `${API}/api/cars/images/logos/atlantic.jpg` },
];

const TestimonialsSection = () => {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % testimonials.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  const t = testimonials[current];

  return (
    <section id="testimonials" className="py-16 sm:py-24 bg-gradient-to-b from-white to-gray-50" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-widest">Trusted Worldwide</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-950 mt-2">What Our Clients Say</h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto text-base">
            Businesses across Africa and beyond trust RunTheRays for end-to-end sourcing, quality control, and logistics.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div
          className="relative max-w-3xl mx-auto"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-10 min-h-[280px] flex flex-col justify-between transition-all duration-500">
            <div>
              <Quote size={32} className="text-orange-500/30 mb-4" />
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed italic" data-testid="testimonial-quote">
                "{t.quote}"
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="font-bold text-blue-950 text-sm" data-testid="testimonial-name">{t.name}</p>
                <p className="text-gray-500 text-xs">{t.title}, <span className="text-orange-600 font-medium">{t.company}</span></p>
              </div>
              {t.rating && (
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={`star-${i}`} size={14} className="fill-orange-400 text-orange-400" />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prev}
              className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600 hover:text-blue-900 hover:border-blue-200 transition-colors"
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  onClick={() => setCurrent(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-orange-500 w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600 hover:text-blue-900 hover:border-blue-200 transition-colors"
              data-testid="testimonial-next"
              aria-label="Next testimonial"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Partner Logos */}
        <div className="mt-14 border-t border-gray-200 pt-10">
          <p className="text-center text-gray-400 text-xs uppercase tracking-widest font-medium mb-8">Our Partners & Clients</p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {partnerLogos.map(logo => (
              <div key={logo.name} className="group" data-testid={`partner-logo-${logo.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center p-3 group-hover:shadow-md group-hover:border-orange-200 transition-all duration-300">
                  <img
                    src={logo.logo}
                    alt={`${logo.name} logo`}
                    className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-all duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
